import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  signal
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TaskPrioritySelectComponent } from '@app/components/task/task-priority-select/task-priority-select.component';
import { TaskProgressSelectComponent } from '@app/components/task/task-state-selectors/task-progress-select/task-progress-select.component';
import { TaskStatusSelectComponent } from '@app/components/task/task-state-selectors/task-status-select/task-status-select.component';
import {
  TaskTimerComponent,
  TaskTimerMode
} from '@app/components/task/task-state-selectors/task-timer/task-timer.component';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TASK_STATUS_DISPLAY } from '@app/shared/types/enums/task-status.constants';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { TaskStatusSelectEnum } from '@app/shared/types/models/task/task-status.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Row width below which the full controls (status label, priority label, progress slider) no longer
 * fit next to a readable title; the row then switches to the mini controls.
 */
const SUBTASK_ITEM_COMPACT_BELOW_PX = 640;

@Component({
  selector: 'proffeo-task-details-subtask-item',
  imports: [
    MatIconModule,
    TaskStatusSelectComponent,
    TaskPrioritySelectComponent,
    MatMenuModule,
    MatTooltip,
    TranslatePipe,
    TaskTimerComponent,
    TaskProgressSelectComponent
  ],
  templateUrl: './task-details-subtask-item.component.html'
})
export class TaskDetailsSubtaskItemComponent {
  private readonly taskActionsService: TasksActionsService = inject(TasksActionsService);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;

  protected readonly TaskStatusSelectEnum = TaskStatusSelectEnum;

  protected readonly TASK_STATUS_DISPLAY = TASK_STATUS_DISPLAY;
  /**
   * The row is too narrow for the full controls: status shows only its dot, priority only its flag
   * and progress only its percentage, so the title keeps room and nothing overflows the card.
   */
  protected readonly isCompact = signal(false);
  protected taskTimerMode = TaskTimerMode;

  public readonly subTask: InputSignal<Task> = input.required<Task>();
  public readonly subTaskClicked: OutputEmitterRef<Task> = output();
  public readonly addTimeReport: OutputEmitterRef<Task> = output();
  public readonly industryClicked = output<Task>();
  public statusChanged = output<TaskStatus>();
  public priorityChanged = output<TaskPriority>();
  public percentageChange = output<number>();

  public constructor() {
    afterNextRender(() => {
      const host = this.hostElement.nativeElement;
      const view = host.ownerDocument.defaultView;
      if (!view?.ResizeObserver) {
        return;
      }

      const observer = new view.ResizeObserver(() => {
        const compact = host.clientWidth > 0 && host.clientWidth < SUBTASK_ITEM_COMPACT_BELOW_PX;
        if (compact !== this.isCompact()) {
          this.isCompact.set(compact);
        }
      });
      observer.observe(host);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  protected copySubtaskLink(): void {
    this.taskActionsService.copyTaskLink(this.subTask());
  }

  protected copySubtaskNumber(): void {
    this.taskActionsService.copyTaskNumber(this.subTask());
  }

  protected deleteSubtask(): void {
    this.taskActionsService.deleteTask(this.subTask()).subscribe();
  }
}
